import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useCurrentUser } from "@/auth/RequireAuth";
import { start, stop, track, type AnalyticsConfig } from "./client";
import { routeTemplate } from "./route";
import { buildTag } from "./tag";

const envConfig: AnalyticsConfig = {
  key: import.meta.env.VITE_POSTHOG_KEY,
  host: import.meta.env.VITE_POSTHOG_HOST,
};

/**
 * Product analytics for the signed-in workspace (spec D2). Starts PostHog for
 * the current user, sends `ui_element_clicked` for every click inside a
 * `data-ph` element, and one `$pageview` per route template. Everything is a
 * no-op when no key is configured.
 */
export function AnalyticsProvider({
  children,
  config = envConfig,
}: {
  children: ReactNode;
  config?: AnalyticsConfig;
}) {
  const user = useCurrentUser();
  const route = routeTemplate(useLocation().pathname);
  const routeRef = useRef(route);
  routeRef.current = route;

  useEffect(() => {
    if (!user) {
      stop();
      return;
    }
    void start(config, user);
    return () => stop();
    // Restart only when the identity changes, not on every new user object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.company_id, config.key, config.host]);

  useEffect(() => {
    // Capture phase: a handler that stops propagation can't hide the click.
    const onClick = (e: MouseEvent) => {
      if (!(e.target instanceof Element)) return;
      const tag = buildTag(e.target);
      if (tag) track("ui_element_clicked", { ...tag, route: routeRef.current });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    track("$pageview", { route });
  }, [route]);

  return <>{children}</>;
}
